import { Autocomplete, TextField } from '@mui/material';
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { useAllEmployees } from '~/data/employees';
import { Employees } from '~/data/employees/types';
import { useVisible } from '~/hooks/useVisible';

type PropsType = {
  label?: string;
  value?: number;
  onChange: (value?: number) => void;
  readOnly?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  error?: boolean;
  helperText?: ReactNode;
};

export const TaskEmployeeSelect = (props: PropsType) => {
  const {
    label,
    value,
    onChange,
    readOnly,
    disabled,
    fullWidth,
    error,
    helperText,
  } = props;

  const ref = useRef<HTMLElement>();

  const optionsVisibility = useVisible(false);
  const { activeEmployees } = useAllEmployees();
  const allEmployees = useMemo(
    () => Object.values(activeEmployees),
    [activeEmployees],
  );

  const [selectedEmployee, setSelectedEmployee] = useState<Employees | null>(
    !!value ? activeEmployees[value] : null,
  );

  const getOptionLabel = useCallback(
    (empOption: Employees) => {
      const employee = activeEmployees[empOption.fEmpId as number];

      return !!employee
        ? `${employee.fEmpFirstName} ${employee.fEmpLastName}, ${employee.fEmpPersonNumber}`
        : '-';
    },
    [activeEmployees],
  );

  const toggleOptionsVisibility = useCallback(
    (inputValue: string, value: Employees | null) => {
      if (
        inputValue.length > 2 &&
        (!value || inputValue !== getOptionLabel(value))
      ) {
        optionsVisibility.show();
      } else {
        optionsVisibility.hide();
      }
    },
    [optionsVisibility, getOptionLabel],
  );

  return (
    <Autocomplete
      ref={ref}
      options={allEmployees}
      getOptionLabel={getOptionLabel}
      fullWidth={fullWidth}
      renderOption={(props, option, index) => {
        return (
          <li {...props} key={`employee-option-${index}-${option.fEmpId}`}>
            {getOptionLabel(option)}
          </li>
        );
      }}
      isOptionEqualToValue={(option, value) => option.fEmpId === value?.fEmpId}
      readOnly={readOnly}
      disabled={disabled}
      value={selectedEmployee || null}
      onChange={(_, newValue) => {
        setSelectedEmployee(newValue);
        onChange(newValue?.fEmpId);
        optionsVisibility.hide();
      }}
      onInputChange={(_event, inputValue) => {
        toggleOptionsVisibility(inputValue, selectedEmployee);
      }}
      open={optionsVisibility.isVisible}
      forcePopupIcon={false}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          helperText={helperText}
          size={'small'}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
        />
      )}
    />
  );
};
